import React from 'react';
import { useUIVersion } from '../contexts/UIVersionContext';
import { IconSettings, IconX, IconToggleLeft, IconToggleRight } from '@tabler/icons-react';
import './ui20-settings.css';

// Define UI features with their metadata
const UI_FEATURES = {
  modernNavigation: {
    name: 'Modern Navigation',
    description: 'Dark top bar and sidebar with modern design',
    category: 'Navigation',
    icon: IconSettings
  },
  modernHomepage: {
    name: 'Modern Homepage',
    description: 'Redesigned homepage with shadcn components and premium styling',
    category: 'Components',
    icon: IconSettings
  },
  modernSystematicTools: {
    name: '10 Systematic Tools',
    description: 'Cleaner design with pagination and improved navigation',
    category: 'Components',
    icon: IconSettings
  },
  modernOrgChart: {
    name: 'Organization Chart',
    description: 'React Flow implementation with black/gold theme and smart loading',
    category: 'Components',
    icon: IconSettings
  },
  modernTrackers: {
    name: 'Enhanced Trackers',
    description: 'Improved tracker UI with better column sizing and performance',
    category: 'Trackers',
    icon: IconSettings
  },
  modernTrackerNotes: {
    name: 'Unified Notes',
    description: 'Cross-tracker notes system with history and labels',
    category: 'Trackers',
    icon: IconSettings
  },
  modernTrackerFilters: {
    name: 'Advanced Filters',
    description: 'Date range and hierarchy filtering for all trackers',
    category: 'Trackers',
    icon: IconSettings
  }
  // Future features can be added here:
  // modernTheme: {
  //   name: 'Modern Theme',
  //   description: 'Updated color scheme and typography',
  //   category: 'Theme',
  //   icon: IconPalette
  // },
};

const FEATURE_CATEGORIES = {
  Navigation: {
    name: 'Navigation',
    description: 'Header and sidebar improvements'
  },
  Theme: {
    name: 'Theme',
    description: 'Colors, typography, and visual styling'
  },
  Components: {
    name: 'Components',
    description: 'Individual component enhancements'
  },
  Trackers: {
    name: 'Trackers',
    description: 'Enhanced tracking system functionality'
  }
};

const UI20SettingsPanel = ({ isOpen, onClose }) => {
  const { toggleFeature, isFeatureEnabled, previewRole, setPreviewRole, isPreviewMode } = useUIVersion();

  const handlePreviewChange = (e) => {
    const newRole = e.target.value || null;
    setPreviewRole(newRole);
    // Close panel after short delay to show the change
    if (newRole !== null) {
      setTimeout(() => onClose(), 300);
    }
  };

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const groupedFeatures = Object.entries(UI_FEATURES).reduce((acc, [key, feature]) => {
    if (!acc[feature.category]) {
      acc[feature.category] = [];
    }
    acc[feature.category].push({ key, ...feature });
    return acc;
  }, {});

  const ToggleButton = ({ enabled, onChange }) => {
    const IconComponent = enabled ? IconToggleRight : IconToggleLeft;
    return (
      <button
        className={`ui20-toggle ${enabled ? 'enabled' : 'disabled'}`}
        onClick={onChange}
        title={enabled ? 'Disable' : 'Enable'}
      >
        <IconComponent size={20} />
      </button>
    );
  };

  return (
    <div className="ui20-settings-overlay" onClick={handleBackdropClick}>
      <div className="ui20-settings-panel">
        {/* Header */}
        <div className="ui20-settings-header">
          <div className="ui20-settings-title">
            <IconSettings size={24} />
            <h2>UI 2.0 Settings</h2>
          </div>
          <button 
            className="ui20-settings-close"
            onClick={onClose}
            title="Close settings"
          >
            <IconX size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="ui20-settings-content">
          <p className="ui20-settings-description">
            Control which UI 2.0 features are enabled. Toggle individual features on or off to customize your experience.
          </p>

          {/* Preview Mode Section */}
          <div className="ui20-feature-category preview-mode-section">
            <div className="ui20-category-header">
              <h3>👁️ Preview Mode</h3>
              <p>Test how different user levels see the platform (sidebar & homepage)</p>
            </div>
            <div className="preview-mode-controls">
              <select
                value={previewRole || ""}
                onChange={handlePreviewChange}
                className="preview-role-select"
              >
                <option value="">✅ My Account ({localStorage.getItem("wb.plan") || "User"})</option>
                <option value="New Agent">👤 New Agent (Free)</option>
                <option value="Agent">🎯 Agent</option>
                <option value="Leader">👥 Leader</option>
                <option value="Broker">💼 Broker</option>
                <option value="Admin">⚙️ Admin</option>
              </select>
              {isPreviewMode && (
                <div className="preview-mode-notice">
                  👁️ Preview Active: <strong>{previewRole}</strong> • Changes are live!
                </div>
              )}
            </div>
          </div>

          {Object.entries(groupedFeatures).map(([categoryKey, features]) => {
            const category = FEATURE_CATEGORIES[categoryKey];
            if (!category) return null;

            return (
              <div key={categoryKey} className="ui20-feature-category">
                <div className="ui20-category-header">
                  <h3>{category.name}</h3>
                  <p>{category.description}</p>
                </div>

                <div className="ui20-feature-list">
                  {features.map((feature) => {
                    const enabled = isFeatureEnabled(feature.key);
                    return (
                      <div key={feature.key} className="ui20-feature-item">
                        <div className="ui20-feature-info">
                          <div className="ui20-feature-name">{feature.name}</div>
                          <div className="ui20-feature-description">{feature.description}</div>
                        </div>
                        <ToggleButton
                          enabled={enabled}
                          onChange={() => toggleFeature(feature.key)}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Footer */}
          <div className="ui20-settings-footer">
            <div className="ui20-settings-version">
              UI 2.0 Control Panel v1.0
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UI20SettingsPanel;